import { z } from 'zod';
import { amountCents, uuid } from './common.js';
import { joinCode } from './teams.js';

/** Stripe refuses EUR charges under €0.50: a total below it is rounded to 0 € and free. */
export const STRIPE_MIN_CHARGE_CENTS = 50;

/** Codes are case-insensitive: normalised to upper case on both sides. */
export const voucherCode = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .transform((s) => s.toUpperCase());

export const voucherKind = z.enum(['percent', 'fixed']);

/** POST /vouchers/validate: preview the discount for the selected formats (P-03). */
export const validateVoucherInput = z.object({
  eventId: uuid,
  code: voucherCode,
  formatIds: z.array(uuid).min(1).max(5),
  /** Teammate's checkout (P-21): price the place at the team's locked tier. */
  joinCode: joinCode.optional(),
});
export type ValidateVoucherInput = z.infer<typeof validateVoucherInput>;

export const voucherPreview = z.object({
  code: z.string(),
  kind: voucherKind,
  value: z.number().int().positive(),
  subtotalCents: amountCents,
  discountCents: amountCents,
  totalCents: amountCents,
});
export type VoucherPreview = z.infer<typeof voucherPreview>;

/**
 * Why a code was refused: `details.reason` of a `voucher_invalid` error, from
 * /vouchers/validate and /checkout alike.
 */
export const voucherErrorReason = z.enum([
  // Unknown code, or outside valid_from / valid_until: one reason, so the buyer isn't told
  // whether a code exists or when it works (decision 0003 D3).
  'invalid',
  'exhausted', // max_uses reached (paid + held orders)
  'not_applicable', // none of the selected formats is eligible
  'too_many_attempts', // rate limit on /vouchers/validate (429)
]);
export type VoucherErrorReason = z.infer<typeof voucherErrorReason>;
